import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, CheckCircle, Package, Settings, Wrench, ChevronLeft, ChevronRight, X, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getAccessoryBySlug, getAccessories } from '../services/productApi';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import CartAddedModal from '../components/CartAddedModal';
import AvailabilityBadge from '../components/AvailabilityBadge';
import { isInquiryOnly as checkInquiryOnly, inquiryUrl } from '../utils/inquiryRules';
import SmartImage from '../components/SmartImage';
import PayPalInstallmentOption from '../components/PayPalInstallmentOption';

const AccessoryDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [accessory, setAccessory] = useState(null);
  const [allAccessories, setAllAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCartModal, setShowCartModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') setLightboxIndex(prev => prev > 0 ? prev - 1 : prev);
      if (e.key === 'ArrowRight') setLightboxIndex(prev => prev + 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen]);

  useEffect(() => {
    const loadAccessory = async () => {
      try {
        const [data, allData] = await Promise.all([
          getAccessoryBySlug(slug),
          getAccessories()
        ]);
        setAccessory(data || null);
        setAllAccessories(allData || []);
      } catch (error) {
        console.error('Error loading accessory:', error);
        setAccessory(null);
        setAllAccessories([]);
      } finally {
        setLoading(false);
      }
    };
    loadAccessory();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Zubehör...</p>
        </div>
      </div>
    );
  }

  if (!accessory) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Zubehör nicht gefunden</h1>
          <Link to="/" className="text-red-600 hover:text-red-700">
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    );
  }

  const isInStock = (accessory.stockQuantity ?? 10) > 0;
  const isInquiryOnly = checkInquiryOnly({ slug: accessory.slug, isAccessory: true });

  const handleAddToCart = () => {
    const cartItem = {
      id: accessory.id,
      slug: accessory.slug,
      name: accessory.name,
      category: accessory.category || null,
      stockQuantity: accessory.stockQuantity,
      pricing: {
        numericPrice: accessory.price,
        buy: formatPrice(accessory.price)
      },
      image: accessory.image,
      type: 'accessory'
    };
    addToCart(cartItem);
    setShowCartModal(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Spec labels in German
  const specLabels = {
    gewicht: 'Gewicht',
    oeffnungsweite: 'Öffnungsweite',
    greifkraft: 'Greifkraft',
    hydraulikdruck: 'Hydraulikdruck',
    kompatibilitaet: 'Kompatibilität',
    schlagenergie: 'Schlagenergie',
    schlagfrequenz: 'Schlagfrequenz',
    arbeitsbreite: 'Arbeitsbreite',
    zinkenanzahl: 'Zinkenanzahl',
    zinkenlaenge: 'Zinkenlänge',
    schalenbreite: 'Schalenbreite',
    greifvolumen: 'Greifvolumen',
    breite: 'Breite',
    tiefe: 'Tiefe',
    volumen: 'Volumen',
    laenge: 'Länge',
    spitzenbreite: 'Spitzenbreite',
    material: 'Material',
    fraesbreite: 'Fräsbreite',
    fraestiefe: 'Frästiefe',
    drehzahl: 'Drehzahl',
    tragfaehigkeit: 'Tragfähigkeit',
    verriegelung: 'Verriegelung',
    wechselzeit: 'Wechselzeit',
    bohrdurchmesser: 'Bohrdurchmesser',
    bohrtiefe: 'Bohrtiefe',
    kehrbreite: 'Kehrbreite',
    buerstendurchmesser: 'Bürstendurchmesser'
  };

  // Build images array from gallery + main image
  const images = (() => {
    const gallery = accessory.gallery && accessory.gallery.length > 0 ? accessory.gallery : [];
    if (gallery.length > 0) return gallery;
    return accessory.image ? [accessory.image] : [];
  })();

  const safeLightboxIndex = Math.min(lightboxIndex, images.length - 1);
  const prevImage = () => setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  const nextImage = () => setCurrentImageIndex(prev => (prev + 1) % images.length);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* LIGHTBOX */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          data-testid="lightbox-overlay"
        >
          <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-50" data-testid="lightbox-close">
            <X size={32} />
          </button>
          <div className="absolute top-4 left-4 text-white/70 text-sm font-medium z-50">
            {safeLightboxIndex + 1} / {images.length}
          </div>
          {images.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev - 1 + images.length) % images.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full transition-all z-50">
              <ChevronLeft size={32} />
            </button>
          )}
          <SmartImage src={images[safeLightboxIndex]} alt={`${accessory.name} - Bild ${safeLightboxIndex + 1}`} className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
          {images.length > 1 && (
            <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => (prev + 1) % images.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white p-3 rounded-full transition-all z-50">
              <ChevronRight size={32} />
            </button>
          )}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-4 py-2">
              {images.map((img, idx) => (
                <button key={idx} onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }} className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${safeLightboxIndex === idx ? 'border-white shadow-lg' : 'border-white/30 hover:border-white/60'}`}>
                  <SmartImage src={img} alt="" className="w-full h-full object-contain bg-white/10 p-0.5" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-red-600 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            <span>Zurück</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Left Column - Image + Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div 
                className="aspect-square bg-gray-50 rounded-lg overflow-hidden relative cursor-pointer"
                onClick={() => { setLightboxIndex(currentImageIndex); setLightboxOpen(true); }}
              >
                <SmartImage
                  src={images[currentImageIndex] || accessory.image}
                  alt={accessory.name}
                  className="w-full h-full object-contain"
                  data-testid="accessory-main-image"
                />
                {/* Navigation arrows */}
                {images.length > 1 && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg">
                      <ChevronLeft size={20} className="text-gray-800" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg">
                      <ChevronRight size={20} className="text-gray-800" />
                    </button>
                  </>
                )}
              </div>
            </div>
            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className={`grid gap-2 ${images.length <= 5 ? 'grid-cols-5' : 'grid-cols-5 sm:grid-cols-8'}`}>
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      currentImageIndex === index ? 'border-red-600 shadow-lg ring-2 ring-red-200' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img src={img} alt={`${accessory.name} ${index + 1}`} className="w-full h-full object-contain p-0.5 bg-white" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Title & Price */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {accessory.name}
              </h1>
              <p className="text-gray-600 mb-4">
                {accessory.description}
              </p>
              
              {/* Price */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="text-sm text-gray-500">Verkaufspreis</span>
                  <p className="text-3xl font-bold text-red-600">
                    {formatPrice(accessory.price)}
                  </p>
                  <span className="text-xs text-gray-500">zzgl. MwSt.</span>
                </div>
              </div>

              {/* Add to Cart Button */}
              {(isInStock || !isInquiryOnly) && (
                <Button
                  onClick={handleAddToCart}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart className="mr-2" size={24} />
                  In den Warenkorb
                </Button>
              )}

              {isInquiryOnly && (
                <Button
                  onClick={() => navigate(inquiryUrl(accessory.name))}
                  className={`w-full py-6 text-lg text-white bg-blue-600 hover:bg-blue-700 ${isInStock ? 'mt-3' : ''}`}
                  data-testid="inquiry-button"
                >
                  <Mail className="mr-2" size={24} />
                  Anfrage stellen
                </Button>
              )}

              {/* PayPal-Ratenzahlung (alle Zubehörteile sind eligible, sofern ≤10k € + sofort lieferbar) */}
              {accessory.price > 0 && (
                <PayPalInstallmentOption
                  product={{
                    slug: accessory.slug,
                    category: accessory.category,
                    name: accessory.name,
                    isAccessory: true,
                    stockQuantity: accessory.stockQuantity,
                  }}
                  netPrice={accessory.price}
                />
              )}
            </div>

            {/* Technical Specs */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gray-50">
                <CardTitle className="flex items-center text-lg">
                  <Settings className="mr-2 text-red-600" size={20} />
                  Technische Daten
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(accessory.specs).map(([key, value]) => (
                    <div key={key} className="border-b border-gray-100 pb-2">
                      <span className="text-xs text-gray-500 block">
                        {specLabels[key] || key}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Einsatzbereich */}
            <Card className="shadow-lg">
              <CardHeader className="bg-gray-50">
                <CardTitle className="flex items-center text-lg">
                  <Wrench className="mr-2 text-red-600" size={20} />
                  Einsatzbereich
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-2">
                  {accessory.einsatzbereich.split(', ').map((bereich, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm"
                    >
                      <CheckCircle size={14} className="mr-1" />
                      {bereich}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Compatibility Info */}
            <Card className="shadow-lg border-2 border-red-100">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Package className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Kompatibilität</h4>
                    <p className="text-sm text-gray-600">
                      Dieses Zubehör ist kompatibel mit den Modellen: <strong>{accessory.specs.kompatibilitaet}</strong>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Other Accessories */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Weiteres Zubehör</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {allAccessories
              .filter(item => item.id !== accessory.id)
              .slice(0, 6)
              .map((item) => (
                <Link
                  key={item.id}
                  to={`/zubehoer/${item.slug}`}
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <div className="aspect-square bg-gray-50 p-2">
                    <SmartImage
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-2 text-center border-t border-gray-100">
                    <span className="text-xs font-medium text-gray-800 block mb-1">
                      {item.name}
                    </span>
                    <span className="text-sm font-bold text-red-600">
                      {Number(item.price || 0).toLocaleString('de-DE')} €
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
      
      {/* Cart Added Modal */}
      <CartAddedModal 
        isOpen={showCartModal}
        onClose={() => setShowCartModal(false)}
        product={{
          id: accessory?.id,
          name: accessory?.name,
          image: accessory?.image,
          pricing: {
            buy: formatPrice(accessory?.price || 0)
          }
        }}
        quantity={1}
      />
    </div>
  );
};

export default AccessoryDetail;
